<script setup lang="ts">
import { ref, defineEmits, defineOptions, onMounted } from "vue";
import { useGeneralStore } from "../stores/general";
import { getCookie, setCookie, deleteCookie } from "../utils/cookies";
import { postMedicalBoothsUsersAffiliation } from "../utils/interfaces";
import GenericButton from "./ui/GenericButton.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import { scrollToInput, debounce } from "../utils/common";
import "@vuepic/vue-datepicker/dist/main.css";

const generalStore = useGeneralStore();

defineOptions({ name: "BaseInformation" });
interface PosibleTab {
  title: string;
  key: string;
  order: number;
  group: string;
}
const emits = defineEmits(["change-view", "pause-timer"]);

const { booth } = generalStore;
const tabSelectedStyle = " text-primary-900 border-b-2 border-primary-900";
const tabSelected = ref("personal");
const groupSelected = ref("");
const posibleGroup = ref([] as string[]);
const possibleTabs = ref([] as PosibleTab[]);
const loading = ref(false);
const errorMessage = ref("");
const sessionEmail = ref("");
const fechaNacimiento = ref();
const sexo = ref("");
const estado = ref("");
const nroOrden = ref();
const email = ref("");
const telefono = ref("");
const canContinue = ref(false);
const userData = ref();
const nombre = ref("");
const apaterno = ref("");
const amaterno = ref("");
const curp = ref("");
const detailMessage = ref("");
const anioNacimiento = ref();
const idRH = ref();
const detailsAnioNacimiento = ref("");

const abreviaturas: { [key: string]: string } = {
  AS: "AGUASCALIENTES",
  BC: "BAJA_CALIFORNIA",
  BS: "BAJA_CALIFORNIA_SUR",
  CC: "CAMPECHE",
  CL: "COAHUILA",
  CM: "COLIMA",
  CS: "CHIAPAS",
  CH: "CHIHUAHUA",
  DF: "CDMX",
  DG: "DURANGO",
  GT: "GUANAJUATO",
  GR: "GUERRERO",
  HG: "HIDALGO",
  JC: "JALISCO",
  MC: "ESTADO_DE_MEXICO",
  MN: "MICHOACAN",
  MS: "MORELOS",
  NT: "NAYARIT",
  NL: "NUEVO_LEON",
  OC: "OAXACA",
  PL: "PUEBLA",
  QT: "QUERETARO",
  QR: "QUINTANA_ROO",
  SP: "SAN_LUIS_POTOSI",
  SL: "SINALOA",
  SR: "SONORA",
  TC: "TABASCO",
  TS: "TAMAULIPAS",
  TL: "TLAXCALA",
  VZ: "VERACRUZ",
  YN: "YUCATAN",
  ZS: "ZACATECAS",
  NE: "NACIDO_EN_EL_EXTRANJERO",
};
type AbreviacionEstado = keyof typeof abreviaturas;

function validarAnioNacimiento() {
  anioNacimiento.value = anioNacimiento.value?.toString().replace(/\D/g, "");
  const anioNum = Number(anioNacimiento.value);
  const anioActual = new Date().getFullYear();
  detailsAnioNacimiento.value = "";

  if (!Number.isInteger(anioNum)) {
    detailsAnioNacimiento.value = "Debe ingresar un número válido.";
    return false;
  }

  if (anioNacimiento.value?.toString().length !== 4) {
    detailsAnioNacimiento.value = "El año debe tener 4 dígitos.";
    return false;
  }

  if (anioNum < 1900) {
    detailsAnioNacimiento.value = "El año no puede ser menor a 1900.";
    return false;
  }

  if (anioNum > anioActual) {
    detailsAnioNacimiento.value = `El año no puede ser mayor al año actual (${anioActual}).`;
    return false;
  }
  return true;
}

function obtenerFechaNacimientoDesdeCURP(curp: string) {
  if (!curp || curp.length < 10) return null;

  const año = curp.substring(4, 6);
  const mes = curp.substring(6, 8);
  const dia = curp.substring(8, 10);

  // Determinar el siglo:
  // Si el año es mayor que el actual (por ejemplo, '99' cuando estamos en 2025), asumimos siglo XX.
  const añoNum = parseInt(año, 10);
  const añoActual = new Date().getFullYear() % 100;
  const siglo = añoNum <= añoActual ? 2000 : 1900;
  const añoCompleto = siglo + añoNum;

  // Crear objeto de fecha
  const fecha = new Date(`${añoCompleto}-${mes}-${dia}`);

  // Validar fecha
  if (isNaN(fecha.getTime())) return null;

  return fecha;
}

const validaTelefono = (telefono: string) => {
  const onlyNumbers = telefono.replace(/\D/g, "");
  const regex = /^[0-9]{10}$/;
  return onlyNumbers === "" || regex.test(onlyNumbers);
};

const validaEmail = (email: string) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

function getLastItemById(array: any[]) {
  return array.sort((a, b) => b.id - a.id)[0];
}

function cleanData() {
  userData.value = null;
  detailMessage.value = "";
  if (generalStore.booth?.tipo !== "TOTEM") {
    email.value = "";
    telefono.value = "";
  }
  canContinue.value = false;
}

async function validar(createIfNotExists = false) {
  loading.value = true;
  let body: postMedicalBoothsUsersAffiliation;
  cleanData();
  errorMessage.value = "";
  detailMessage.value = "";
  canContinue.value = false;

  if (!validaTelefono(telefono.value)) {
    errorMessage.value = "El campo teléfono debe tener 10 dígitos";
    loading.value = false;
    return;
  }
  let telefonoStr = telefono.value.replace(/\D/g, "");
  if (generalStore.booth?.tipo === "TOTEM") {
    if (!telefonoStr || !validaEmail(email.value)) {
      errorMessage.value = "Los campos teléfono y email son obligatorios";
      loading.value = false;
      return;
    }
  }
  let estadoCURP: AbreviacionEstado = "AS";
  if (tabSelected.value === "curp") {
    const booth = getCookie("booth") as string;
    if (!curp.value) {
      errorMessage.value = "El campo CURP es obligatorio";
      loading.value = false;
      return;
    }
    if (curp.value.length !== 18) {
      errorMessage.value = "El campo CURP debe tener 18 caracteres";
      loading.value = false;
      return;
    }
    const fechaNacimiento = obtenerFechaNacimientoDesdeCURP(curp.value);
    if (!fechaNacimiento) {
      errorMessage.value = "El campo CURP no es válido";
      loading.value = false;
      return;
    }
    const sexo = curp.value.substring(10, 11);
    const genero = sexo === "H" ? "MASCULINO" : "FEMENINO";
    estadoCURP = curp.value.substring(11, 13);
    body = {
      claveCabina: booth,
      fechaNacimiento: fechaNacimiento.toISOString(),
      genero: genero,
      curp: curp.value,
      telefono: telefonoStr,
      email: email.value,
    };
  } else if (tabSelected.value === "idEmpleadoAnio") {
    body = {
      claveCabina: getCookie("booth") as string,
      idRH: idRH.value,
      anioNacimiento: parseInt(anioNacimiento.value, 10),
    };
  } else {
    if (!fechaNacimiento.value || !sexo.value) {
      errorMessage.value = "Los campos marcados con * son obligatorios";
      loading.value = false;
      return;
    }
    const booth = getCookie("booth") as string;
    body = {
      claveCabina: booth,
      fechaNacimiento: fechaNacimiento.value.toISOString(),
      genero: sexo.value as "MASCULINO" | "FEMENINO",
      nombres: nombre.value,
      apellidoPaterno: apaterno.value,
      apellidoMaterno: amaterno.value,
      estado: estado.value,
      paisNacimiento: "México",
      telefono: telefonoStr,
      email: email.value,
    };
  }
  if (generalStore.booth?.solicitarNroOrden) {
    if (!nroOrden.value) {
      errorMessage.value = "El campo número de orden es obligatorio";
      loading.value = false;
      return;
    } else {
      body.nroOrden = nroOrden.value;
    }
  }
  const { idContratoCliente, idPlanCliente } = generalStore.booth;
  const hasClientBase = idContratoCliente && idPlanCliente;
  const query = {} as { onlySearch?: boolean };
  if (hasClientBase && !createIfNotExists) {
    query.onlySearch = true;
  }
  const res = (await generalStore.postMedicalBoothsAffiliation(
    body,
    query
  )) as any;
  if (res && res.afiliacion) {
    if (hasClientBase) {
      const numeroPlanAsociado = res.afiliacion.numeroPlanAsociado;
      const contratoPlanCliente = `${idContratoCliente}-${`${idPlanCliente}`.padStart(
        3,
        "0"
      )}-`;
      if (numeroPlanAsociado.startsWith(contratoPlanCliente)) {
        const hasNombreUsuario = res.afiliacion.afiliado.nombreUsuario;
        const nombreUsuarioIsEmail = validaEmail(hasNombreUsuario);
        const telefonoAfiliado = getLastItemById(
          res.afiliacion.afiliado.telefonos
        );
        if (telefonoAfiliado) {
          const newPhoneValue =
            hasNombreUsuario && !nombreUsuarioIsEmail
              ? hasNombreUsuario
              : telefonoAfiliado.numero;
          telefono.value = newPhoneValue.replace(/\D/g, "").slice(-10);
          telefonoStr = newPhoneValue.replace(/\D/g, "").slice(-10);
        }
        const emailAfiliado = getLastItemById(res.afiliacion.afiliado.emails);
        if (emailAfiliado) {
          const newEmailValue =
            hasNombreUsuario && nombreUsuarioIsEmail
              ? hasNombreUsuario
              : emailAfiliado.email;
          email.value = newEmailValue.trim();
        }
      } else {
        detailMessage.value =
          tabSelected.value === "idEmpleadoAnio"
            ? "Datos no encontrados, asegure que sus datos sean correctos o pruebe como invitado"
            : "No se encontró un expediente asociado al afiliado por lo que se creará uno nuevo";
      }
    }
    userData.value = {
      ...res,
      nroOrden: nroOrden.value,
      telefono: telefonoStr,
      email: email.value,
    };
    canContinue.value = true;
  } else {
    if (query.onlySearch) {
      detailMessage.value =
        tabSelected.value === "idEmpleadoAnio"
          ? "Datos no encontrados, asegure que sus datos sean correctos o pruebe como invitado"
          : "No se encontró un expediente asociado al afiliado por lo que se creará uno nuevo";
      if (tabSelected.value === "personal") {
        canContinue.value = true;
      }
    } else {
      errorMessage.value = res.message?.details;
    }
    if (tabSelected.value === "curp") {
      fechaNacimiento.value = obtenerFechaNacimientoDesdeCURP(curp.value);
      tabSelected.value = "personal";
      if (body.genero) {
        sexo.value = body.genero;
      }
      if (generalStore.booth?.solicitarNroOrden) {
        nroOrden.value = body.nroOrden;
      }
      if (abreviaturas[estadoCURP]) {
        estado.value = abreviaturas[estadoCURP];
      }
    }
  }
  loading.value = false;
}

async function handleClick() {
  if (!document || !document.exitFullscreen) return;
  await document.exitFullscreen();
}

const formatDate = (date: Date) => {
  if (!date) return "";
  const day = date.getDate();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

async function changeView(e: Event) {
  e.preventDefault();
  if (canContinue.value) {
    const idPersona = userData.value?.afiliacion?.idPersona;
    if (!idPersona) {
      await validar(true);
    } else {
      const promises = [];
      const existPhoneNumber =
        userData.value.afiliacion?.afiliado?.telefonos?.some(
          (phone: any) => phone.numero.replace(/\D/g, "") === telefono.value
        );
      if (!existPhoneNumber) {
        promises.push(
          generalStore.addPhoneToPerson(idPersona, {
            numero: `521${telefono.value}`,
            tipoTelefono: "PARTICULAR",
            tipoLinea: "CELULAR",
            tieneWA: false,
          })
        );
      }
      const existEmail = userData.value.afiliacion?.afiliado?.emails?.some(
        (item: any) => item.email === email.value
      );
      if (!existEmail) {
        promises.push(
          generalStore.addEmailToPerson(idPersona, {
            email: email.value,
            tipoEmail: "PARTICULAR",
          })
        );
      }
      await Promise.allSettled(promises);
    }
    userData.value = {
      ...userData.value,
      email: email.value,
    };
    emits("change-view", "showServices", userData.value);
  }
}

const tryValidate = debounce(() => {
  if (generalStore.booth?.tipo === "TOTEM") {
    if (!email.value || !telefono.value) return;
  }
  if (tabSelected.value === "curp") {
    if (curp.value.length !== 18) return;
  } else if (tabSelected.value === "personal") {
    if (
      !nombre.value ||
      !apaterno.value ||
      !amaterno.value ||
      !fechaNacimiento.value ||
      !sexo.value
    )
      return;
  } else if (tabSelected.value === "idEmpleadoAnio") {
    if (!anioNacimiento.value || !idRH.value || !validarAnioNacimiento())
      return;
  }
  validar();
}, 1000);

const groupName = {
  colaborador: "Soy Colaborador",
  posibleInvitado: "Soy Invitado",
} as Record<string, string>;

const handleChangeGroup = (group: string) => {
  groupSelected.value = group;
  tabSelected.value = possibleTabs.value.filter(
    (f) => f.group === group
  )[0].key;
  cleanData();
};

onMounted(() => {
  emits("pause-timer");
  possibleTabs.value = [];
  Object.entries(generalStore.getBooth.customConfig?.tipoValidacion).forEach(
    ([key, value]) => {
      if (value) {
        switch (key) {
          case "datosPersonales":
            possibleTabs.value.push({
              title: "Datos Personales",
              key: "personal",
              order: 2,
              group: "posibleInvitado",
            });
            break;
          case "curp":
            possibleTabs.value.push({
              title: "CURP",
              key: "curp",
              order: 3,
              group: "posibleInvitado",
            });
            break;
          case "idEmpleadoAnio":
            possibleTabs.value.push({
              title: "Soy Colaborador",
              key: "idEmpleadoAnio",
              order: 1,
              group: "colaborador",
            });
            break;
        }
      }
    }
  );

  possibleTabs.value.sort((a, b) => a.order - b.order);
  tabSelected.value = possibleTabs.value[0].key;
  posibleGroup.value = [];
  possibleTabs.value.forEach(({ group }) => {
    if (!posibleGroup.value.includes(group)) {
      posibleGroup.value.push(group);
    }
  });
  groupSelected.value = posibleGroup.value[0];
  const booth = getCookie("booth") as string;
  const fingerprint = getCookie("fingerprint") as string;
  generalStore.getSession().then((session: any) => {
    const accessToken = session.tokens?.accessToken as object;
    sessionEmail.value = session.tokens?.signInDetails?.loginId?.split("@")[0];
    if (!booth) {
      emits("change-view", "setBoothKey");
      return;
    }
    if (!fingerprint) {
      emits("change-view", "login");
      return;
    }
    const days = generalStore.getBooth.customConfig?.duracionSesion;
    setCookie("booth", booth, days);
    setCookie("fingerprint", fingerprint, days);
    generalStore
      .postMedicalBoothsValidation({
        claveCabina: booth,
        fingerprint: fingerprint,
        accessToken: accessToken,
      })
      .then((res: any) => {
        if (res.code !== 200) {
          const { idSesion } = res.message;
          if (idSesion) {
            generalStore.deleteBoothSession(idSesion);
          }
          deleteCookie("booth");
          deleteCookie("fingerprint");
          emits("change-view", "setBoothKey");
        }
      })
      .catch((err: any) => {
        console.log(err);
      });
  });
});
</script>

<template>
  <div class="flex flex-col items-center w-full">
    <div class="absolute top-1 left-1 text-[10px]">
      <p>{{ sessionEmail }}</p>
      <p>{{ booth.nombre }}</p>
    </div>
    <GenericButton
      variant="ghost"
      @click="handleClick"
      class="absolute top-0 right-0"
    >
      Salir
    </GenericButton>
    <div class="rounded-lg shadow-lg border border-gray-300 sm:w-2/3 w-full">
      <h1 id="titulo" class="text-2xl text-primary-900 font-bold p-4 pb-0">
        Registro para aplicar estudios
      </h1>
      <hr class="my-4 border-gray-200" />
      <p class="text-center">Por favor, selecciona el método de registro:</p>
      <div
        class="flex items-center my-4 mx-6 justify-center"
        v-if="booth?.idContratoCliente"
      >
        <!-- <img src="@/assets/warning.png" alt="warning" class="w-12 h-12 mr-2" /> -->
        <div class="text-sm font-bold">
          <p>Por favor, asegúrate de ingresar correctamente todos tus datos.</p>
          <p>
            Esto nos permitirá identificar tu expediente o generar uno nuevo si
            aún no cuentas con él.
          </p>
        </div>
      </div>

      <div
        v-if="posibleGroup.length > 1"
        class="flex justify-around my-2 w-full"
      >
        <GenericButton
          v-for="group in posibleGroup"
          :key="group"
          :variant="groupSelected === group ? 'secondary' : 'outline'"
          class="px-2! py-1!"
          @click="handleChangeGroup(group)"
        >
          {{ groupName[group] }}
        </GenericButton>
      </div>

      <div
        v-if="possibleTabs.filter((f) => f.group === groupSelected).length > 1"
        class="grid mb-4"
        :class="{
          'grid-cols-2!':
            possibleTabs.filter((f) => f.group === groupSelected).length === 2,
          'grid-cols-3!':
            possibleTabs.filter((f) => f.group === groupSelected).length === 3,
        }"
      >
        <button
          v-for="possibleTab in possibleTabs.filter(
            (f) => f.group === groupSelected
          )"
          :key="possibleTab.key"
          :class="
            'text-gray-600 py-4 px-6 block hover:text-primary focus:outline-none cursor-pointer' +
            (tabSelected === possibleTab.key ? tabSelectedStyle : '')
          "
          @click="tabSelected = possibleTab.key"
        >
          {{ possibleTab.title }}
        </button>
      </div>

      <form
        v-if="tabSelected === 'idEmpleadoAnio'"
        class="flex flex-col items-center gap-3 p-4 pt-0"
        @submit="changeView"
        novalidate
      >
        <div
          class="grid grid-cols-1 md:grid-cols-2 gap-3 w-full"
          :class="{ 'grid-cols-3!': generalStore.booth?.solicitarNroOrden }"
        >
          <div>
            <label for="curp" class="block text-sm font-medium text-primary-900"
              >Número de empleado *</label
            >
            <input
              @focus="scrollToInput"
              @input="tryValidate"
              id="idRH"
              name="idRH"
              type="text"
              maxlength="18"
              autocomplete="off"
              placeholder="XXXXXXXXXX"
              class="w-full border border-gray-300 rounded p-2"
              v-model="idRH"
              required
            />
          </div>

          <div>
            <label
              for="email"
              class="block text-sm font-medium text-primary-900"
            >
              Año de Nacimiento *
            </label>
            <input
              @focus="scrollToInput"
              @input="tryValidate"
              class="w-full border border-gray-300 rounded p-2"
              type="number"
              id="anioNacimiento"
              name="anioNacimiento"
              placeholder="XXXX"
              autocomplete="off"
              v-model="anioNacimiento"
              required
              min="1900"
              @blur="validarAnioNacimiento"
            />
            <span class="text-red-500">{{ detailsAnioNacimiento }}</span>
          </div>

          <div v-if="generalStore.booth?.solicitarNroOrden">
            <label
              for="nroOrden"
              class="block text-sm font-medium text-primary-900"
              >Número de orden *</label
            >
            <input
              @focus="scrollToInput"
              class="w-full border border-gray-300 rounded p-2"
              type="text"
              id="nroOrden"
              name="nroOrden"
              placeholder="XXXXXX"
              autocomplete="off"
              required
              v-model="nroOrden"
            />
          </div>

          <div>
            <label
              for="email"
              class="block text-sm font-medium text-primary-900"
            >
              Correo Electrónico
              {{ generalStore.booth?.tipo === "TOTEM" ? "*" : "" }}
            </label>
            <input
              @focus="scrollToInput"
              class="w-full border border-gray-300 rounded p-2"
              type="email"
              id="email"
              name="email"
              placeholder="correo@ejemplo.com"
              autocomplete="off"
              v-model="email"
              :disabled="!canContinue"
              :required="generalStore.booth?.tipo === 'TOTEM'"
            />
          </div>

          <div>
            <label
              for="telefono"
              class="block text-sm font-medium text-primary-900"
            >
              Teléfono {{ generalStore.booth?.tipo === "TOTEM" ? "*" : "" }}
            </label>
            <input
              @focus="scrollToInput"
              class="w-full border border-gray-300 rounded p-2"
              type="tel"
              id="telefono"
              name="telefono"
              pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
              placeholder="(XXX) XXX-XXXX"
              autocomplete="off"
              v-model="telefono"
              :disabled="!canContinue"
              :required="generalStore.booth?.tipo === 'TOTEM'"
            />
          </div>
        </div>

        <p v-if="errorMessage" class="text-red-500">{{ errorMessage }}</p>
        <p v-if="detailMessage" class="text-lg text-center underline">
          {{ detailMessage }}
        </p>
        <GenericButton
          type="submit"
          :loading="loading"
          :disabled="!canContinue"
          class="w-full"
        >
          Continuar
        </GenericButton>
      </form>

      <form
        v-if="tabSelected === 'curp'"
        class="flex flex-col items-center gap-3 p-4 pt-0"
        @submit="changeView"
        novalidate
      >
        <div
          class="grid grid-cols-2 gap-3 w-full"
          :class="{ 'grid-cols-3!': !generalStore.booth?.solicitarNroOrden }"
        >
          <div>
            <label for="curp" class="block text-sm font-medium text-primary-900"
              >CURP *</label
            >
            <input
              @focus="scrollToInput"
              id="curp"
              name="curp"
              type="text"
              maxlength="18"
              autocomplete="off"
              placeholder="XXXX000000XXXXXX00"
              style="text-transform: uppercase"
              class="w-full border border-gray-300 rounded p-2"
              required
              v-model="curp"
              @input="tryValidate"
              @v-model:update="(e: string) => (curp = e.toUpperCase())"
            />
          </div>

          <div v-if="generalStore.booth?.solicitarNroOrden">
            <label
              for="nroOrden"
              class="block text-sm font-medium text-primary-900"
              >Número de orden *</label
            >
            <input
              @focus="scrollToInput"
              class="w-full border border-gray-300 rounded p-2"
              type="text"
              id="nroOrden"
              name="nroOrden"
              placeholder="XXXXXX"
              autocomplete="off"
              required
              v-model="nroOrden"
            />
          </div>

          <div>
            <label
              for="email"
              class="block text-sm font-medium text-primary-900"
            >
              Correo Electrónico
              {{ generalStore.booth?.tipo === "TOTEM" ? "*" : "" }}
            </label>
            <input
              @focus="scrollToInput"
              class="w-full border border-gray-300 rounded p-2"
              type="email"
              id="email"
              name="email"
              placeholder="correo@ejemplo.com"
              autocomplete="off"
              v-model="email"
              :disabled="!canContinue"
              :required="generalStore.booth?.tipo === 'TOTEM'"
            />
          </div>

          <div>
            <label
              for="telefono"
              class="block text-sm font-medium text-primary-900"
            >
              Teléfono {{ generalStore.booth?.tipo === "TOTEM" ? "*" : "" }}
            </label>
            <input
              @focus="scrollToInput"
              class="w-full border border-gray-300 rounded p-2"
              type="tel"
              id="telefono"
              name="telefono"
              pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
              placeholder="(XXX) XXX-XXXX"
              autocomplete="off"
              v-model="telefono"
              :disabled="!canContinue"
              :required="generalStore.booth?.tipo === 'TOTEM'"
            />
          </div>
        </div>
        <p v-if="errorMessage" class="text-red-500">{{ errorMessage }}</p>
        <p v-if="detailMessage" class="text-lg text-center underline">
          {{ detailMessage }}
        </p>
        <GenericButton
          type="submit"
          :loading="loading"
          :disabled="!canContinue"
          class="w-full"
        >
          Continuar
        </GenericButton>
      </form>

      <form
        v-if="tabSelected === 'personal'"
        class="grid grid-cols-3 gap-3 p-4 pt-0"
        @submit="changeView"
        novalidate
      >
        <div v-if="generalStore.booth?.solicitarNroOrden">
          <label
            for="nroOrden"
            class="block text-sm font-medium text-primary-900"
            >Número de orden *</label
          >
          <input
            @focus="scrollToInput"
            @input="tryValidate"
            class="w-full border border-gray-300 rounded p-2"
            type="text"
            id="nroOrden"
            name="nroOrden"
            placeholder="XXXXXX"
            autocomplete="off"
            required
            v-model="nroOrden"
          />
        </div>

        <div>
          <label for="nombre" class="block text-sm font-medium text-primary-900"
            >Nombre *</label
          >
          <input
            @focus="scrollToInput"
            @input="tryValidate"
            class="w-full border border-gray-300 rounded p-2"
            type="text"
            id="nombre"
            name="nombre"
            placeholder="Tu nombre"
            autocomplete="off"
            required
            v-model="nombre"
          />
        </div>

        <div>
          <label
            for="apaterno"
            class="block text-sm font-medium text-primary-900"
            >Apellido Paterno *</label
          >
          <input
            @focus="scrollToInput"
            @input="tryValidate"
            class="w-full border border-gray-300 rounded p-2"
            type="text"
            id="apaterno"
            name="apaterno"
            placeholder="Tu apellido paterno"
            autocomplete="off"
            required
            v-model="apaterno"
          />
        </div>

        <div>
          <label
            for="amaterno"
            class="block text-sm font-medium text-primary-900"
            >Apellido Materno *</label
          >
          <input
            @focus="scrollToInput"
            @input="tryValidate"
            class="w-full border border-gray-300 rounded p-2"
            type="text"
            id="amaterno"
            name="amaterno"
            placeholder="Tu apellido materno"
            autocomplete="off"
            required
            v-model="amaterno"
          />
        </div>

        <div>
          <label for="estado" class="block text-sm font-medium text-primary-900"
            >Lugar de Nacimiento *</label
          >
          <select
            name="estado"
            id="estado"
            v-model="estado"
            @input="tryValidate"
            class="w-full border border-gray-300 rounded p-2"
          >
            <option value="" disabled selected>Lugar de nacimiento</option>
            <option value="NO_ESPECIFICADO">Extranjero</option>
            <option value="AGUASCALIENTES">Aguascalientes</option>
            <option value="BAJA_CALIFORNIA">Baja California</option>
            <option value="BAJA_CALIFORNIA_SUR">Baja California Sur</option>
            <option value="CAMPECHE">Campeche</option>
            <option value="COAHUILA">Coahuila</option>
            <option value="COLIMA">Colima</option>
            <option value="CHIAPAS">Chiapas</option>
            <option value="CHIHUAHUA">Chihuahua</option>
            <option value="CDMX">Ciudad de México</option>
            <option value="GUANAJUATO">Guanajuato</option>
            <option value="GUERRERO">Guerrero</option>
            <option value="HIDALGO">Hidalgo</option>
            <option value="JALISCO">Jalisco</option>
            <option value="ESTADO_DE_MEXICO">Estado de México</option>
            <option value="MICHOACAN">Michoacán</option>
            <option value="MORELOS">Morelos</option>
            <option value="NAYARIT">Nayarit</option>
            <option value="NUEVO_LEON">Nuevo León</option>
            <option value="OAXACA">Oaxaca</option>
            <option value="PUEBLA">Puebla</option>
            <option value="QUERETARO">Querétaro</option>
            <option value="QUINTANA_ROO">Quintana Roo</option>
            <option value="SAN_LUIS_POTOSI">San Luis Potosí</option>
            <option value="SINALOA">Sinaloa</option>
            <option value="SONORA">Sonora</option>
            <option value="TABASCO">Tabasco</option>
            <option value="TAMAULIPAS">Tamaulipas</option>
            <option value="TLAXCALA">Tlaxcala</option>
            <option value="VERACRUZ">Veracruz</option>
            <option value="YUCATAN">Yucatán</option>
            <option value="ZACATECAS">Zacatecas</option>
          </select>
        </div>

        <div>
          <label
            for="fechaNacimiento"
            class="block text-sm font-medium text-primary-900"
            >Fecha de Nacimiento *</label
          >
          <VueDatePicker
            v-model="fechaNacimiento"
            locale="es"
            timezone="UTC"
            :format="formatDate"
            :max-date="new Date()"
            @update:model-value="tryValidate()"
          >
            <template #time-picker=""></template>
            <template #action-row="{ internalModelValue, selectDate }">
              <div class="flex flex-row items-center justify-between w-[260px]">
                <p>{{ formatDate(internalModelValue as Date) }}</p>
                <button
                  class="bg-primary text-white p-1 px-2 rounded"
                  @click="selectDate"
                >
                  Aceptar
                </button>
              </div>
            </template>
          </VueDatePicker>
          <!-- <input @focus="scrollToInput" class="w-full border border-gray-300 rounded p-2" type="date" id="fechaNacimiento" v-model="fechaNacimiento" name="fechaNacimiento" required> -->
        </div>

        <div>
          <label for="sexo" class="block text-sm font-medium text-primary-900"
            >Sexo *</label
          >
          <select
            v-model="sexo"
            class="w-full border border-gray-300 rounded p-2"
            id="sexo"
            name="sexo"
            required
            @input="tryValidate"
          >
            <option value="" disabled selected>Sexo</option>
            <option value="FEMENINO">Femenino</option>
            <option value="MASCULINO">Masculino</option>
          </select>
        </div>

        <div>
          <label for="email" class="block text-sm font-medium text-primary-900">
            Correo Electrónico
            {{ generalStore.booth?.tipo === "TOTEM" ? "*" : "" }}
          </label>
          <input
            @focus="scrollToInput"
            class="w-full border border-gray-300 rounded p-2"
            type="email"
            id="email"
            name="email"
            placeholder="correo@ejemplo.com"
            autocomplete="off"
            v-model="email"
            :disabled="!canContinue"
            :required="generalStore.booth?.tipo === 'TOTEM'"
          />
        </div>

        <div>
          <label
            for="telefono"
            class="block text-sm font-medium text-primary-900"
          >
            Teléfono {{ generalStore.booth?.tipo === "TOTEM" ? "*" : "" }}
          </label>
          <input
            @focus="scrollToInput"
            class="w-full border border-gray-300 rounded p-2"
            type="tel"
            id="telefono"
            name="telefono"
            pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
            placeholder="(XXX) XXX-XXXX"
            autocomplete="off"
            v-model="telefono"
            :disabled="!canContinue"
            :required="generalStore.booth?.tipo === 'TOTEM'"
          />
        </div>
        <p v-if="errorMessage" class="text-red-500 col-span-3 text-center">
          {{ errorMessage }}
        </p>
        <p
          v-if="detailMessage"
          class="col-span-3 text-center text-lg text-center underline"
        >
          {{ detailMessage }}
        </p>
        <GenericButton
          :loading="loading"
          :disabled="!canContinue"
          class="col-span-3"
          type="submit"
        >
          Continuar
        </GenericButton>
      </form>
    </div>
  </div>
</template>

<style scoped></style>
